import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError, type BrandInfo, type Lang, type Me, type PublicBrand } from "./api";
import { type Key, type Vars, resolveLang, storeLang, translate } from "./i18n";
import { applyBrand } from "./theme";

export interface Session {
  /** undefined while loading. */
  brandInfo: BrandInfo | undefined;
  brand: PublicBrand | null;
  /** undefined while loading, null when signed out. */
  me: Me | null | undefined;
  lang: Lang;
  setLang: (lang: Lang) => void;
  setMe: (me: Me | null) => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  t: (key: Key, vars?: Vars) => string;
  /** Set when the first load failed (network, API down). */
  loadError: string | null;
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [brandInfo, setBrandInfo] = useState<BrandInfo | undefined>(undefined);
  const [me, setMeState] = useState<Me | null | undefined>(undefined);
  const [lang, setLangState] = useState<Lang>(() => resolveLang());
  const [loadError, setLoadError] = useState<string | null>(null);

  const setMe = useCallback((next: Me | null) => {
    setMeState(next);
    if (next) setLangState(resolveLang(next.identity.language));
  }, []);

  const setLang = useCallback((next: Lang) => {
    storeLang(next);
    setLangState(next);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const info = await api.brand();
      setBrandInfo(info);
      applyBrand(info.brand);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "failed");
      setBrandInfo((prev) => prev ?? { brand: null, brands: [], staging: null });
    }
    try {
      setMe(await api.me());
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 400)) setMeState(null);
      else setMeState((prev) => prev ?? null);
    }
  }, [setMe]);

  const signOut = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setMeState(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<Session>(
    () => ({
      brandInfo,
      brand: brandInfo?.brand ?? null,
      me,
      lang,
      setLang,
      setMe,
      refresh,
      signOut,
      t: (key, vars) => translate(lang, key, vars),
      loadError,
    }),
    [brandInfo, me, lang, setLang, setMe, refresh, signOut, loadError],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession outside SessionProvider");
  return ctx;
}

/** The first name to greet with: contact, identity name, else the part of the email before @. */
export function firstNameOf(me: Me): string {
  if (me.contact?.firstName) return me.contact.firstName;
  if (me.identity.name) return me.identity.name.split(/\s+/)[0] ?? me.identity.name;
  return me.identity.email.split("@")[0] ?? me.identity.email;
}
